import React from "react";
import { LegalLayout, Section, P, BulletList, InfoCard, Callout } from "./shared";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="How Xenwealth Group collects, uses and protects personal data across our website, advisory services and Xenwealth Habit Tracker, in line with the Personal Data Protection Act 2010 (Malaysia)."
      meta={[
        ["Effective", "1 January 2026"],
        ["Last updated", "6 September 2026"],
        ["Applies to", "xenwealth.com.my, our advisory services & Xenwealth Habit Tracker"],
      ]}
      crossHref="/terms"
      crossLabel="Terms of Service"
      ctaHref="/#contact"
      ctaEyebrow="Data protection enquiries"
      ctaLabel="Get in touch"
      tocItems={[
        ["p1", "1. Who we are"],
        ["p2", "2. Data we collect"],
        ["p3", "3. How we use your data"],
        ["p4", "4. Legal basis & consent"],
        ["p5", "5. Disclosure to third parties"],
        ["p6", "6. Data security"],
        ["p7", "7. Retention"],
        ["p8", "8. Your rights"],
        ["p9", "9. Cookies & analytics"],
        ["p10", "10. Cross-border transfers"],
        ["p11", "11. Changes to this policy"],
        ["p12", "12. Google Calendar & Xenwealth Habit Tracker"],
        ["p13", "13. Contact us"],
      ]}
    >
      <Section id="p1" heading="1. Who we are">
        <P>Xenwealth Group (“Xenwealth”, “we”, “us”) is a financial advisory firm based in Petaling Jaya, Selangor, Malaysia. We provide advisory and consulting services to individuals, families and businesses.</P>
        <P last>This policy explains what personal data we collect when you visit our website, enquire about our services, or engage us as a client — and what we do with it.</P>
      </Section>

      <Section id="p2" heading="2. Data we collect">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: "16px", marginBottom: "16px" }}>
          <InfoCard label="Identity & contact">Name, email address, phone number, postal address, employer or business name.</InfoCard>
          <InfoCard label="Financial & advisory">Information you choose to share so we can advise you — income, assets, liabilities, dependants, goals, existing policies or plans.</InfoCard>
          <InfoCard label="Technical">IP address, browser and device type, pages viewed, referring source, and cookie identifiers.</InfoCard>
          <InfoCard label="Recruitment">If you apply to join us: your CV, work history, qualifications and any details you submit.</InfoCard>
        </div>
        <P last>We do not knowingly collect data from anyone under 18 without the consent of a parent or guardian.</P>
      </Section>

      <Section id="p3" heading="3. How we use your data">
        <BulletList
          items={[
            "To respond to your enquiries and arrange consultations.",
            "To assess your circumstances and provide advisory recommendations suited to you.",
            "To administer and service your engagement with us, including review meetings.",
            "To meet legal, regulatory, tax, audit and anti-money-laundering obligations.",
            "To improve our website and understand how it is used.",
            "To send you updates or invitations, where you have asked to receive them.",
          ]}
        />
      </Section>

      <Section id="p4" heading="4. Legal basis & consent">
        <P>We process personal data with your consent, or where processing is necessary to perform a contract with you, to comply with a legal obligation, or for our legitimate business interests.</P>
        <P last>Where consent is the basis, you may withdraw it at any time by writing to us. Withdrawing consent may mean we can no longer provide certain services to you.</P>
      </Section>

      <Section id="p5" heading="5. Disclosure to third parties">
        <P>We do not sell your personal data. We may share it with:</P>
        <BulletList
          items={[
            "Product providers, insurers, trustees or fund houses, where needed to act on your instructions.",
            "Professional advisers — auditors, lawyers, accountants — bound by confidentiality.",
            "IT, hosting, email and analytics service providers acting on our instructions.",
            "Regulators, courts or authorities where we are required by law to disclose.",
          ]}
        />
      </Section>

      <Section id="p6" heading="6. Data security">
        <P last>We apply practical administrative, technical and physical safeguards to protect personal data against loss, misuse and unauthorised access — including access controls, encrypted transmission and staff confidentiality obligations. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.</P>
      </Section>

      <Section id="p7" heading="7. Retention">
        <P last>We keep personal data only as long as needed for the purposes described here, or as required by law and regulatory record-keeping rules. When data is no longer needed, we securely delete or anonymise it.</P>
      </Section>

      <Section id="p8" heading="8. Your rights">
        <P>Under the PDPA 2010, you may request access to your personal data, ask us to correct inaccurate data, limit how we process it, or object to direct marketing. Write to us using the details in section 13 and we will respond within a reasonable time.</P>
        <P last>We may need to verify your identity before acting on a request, and a small fee may apply to data access requests where permitted.</P>
      </Section>

      <Section id="p9" heading="9. Cookies & analytics">
        <P last>Our website uses cookies to keep the site working properly and to understand aggregate usage. You can block or delete cookies in your browser settings; some parts of the site may then not work as intended.</P>
      </Section>

      <Section id="p10" heading="10. Cross-border transfers">
        <P last>Some of our service providers store data on servers outside Malaysia. Where data is transferred abroad, we take reasonable steps to ensure it receives protection comparable to that required under Malaysian law.</P>
      </Section>

      <Section id="p11" heading="11. Changes to this policy">
        <P last>We may update this policy from time to time. The revised version will be posted on this page with a new “last updated” date. Material changes affecting existing clients will be communicated directly.</P>
      </Section>

      <Section id="p12" heading="12. Google Calendar & Xenwealth Habit Tracker">
        <P>Xenwealth Habit Tracker is an internal productivity and activity-management application used by authorised Xenwealth team members to record work activities such as appointments, meetings, recruitment activities, events and other work-related tasks. See our <a href="/habit-tracker" style={{ color: "#b9860a", fontWeight: 600 }}>Xenwealth Habit Tracker</a> page for more information about the platform.</P>
        <P>Habit Tracker offers an optional integration with Google Calendar. If you choose to connect your Google account, you will be asked to authorise access through Google's own OAuth consent screen — nothing is connected without your explicit permission, and you may decline or discontinue at any time.</P>
        <P>Where you authorise the integration, Habit Tracker accesses the calendar information reasonably necessary to identify and import your selected events, together with the event details required for activity tracking. We only access what is needed for this purpose.</P>
        <P>This calendar information is used solely to:</P>
        <BulletList
          items={[
            "Import authorised Google Calendar events into Habit Tracker;",
            "Categorise those activities according to the event colours/categories you have chosen to map;",
            "Keep your selected Google Calendar events synchronised with Habit Tracker.",
          ]}
        />
        <P>The integration works in one direction only: Google Calendar → Xenwealth Habit Tracker. Habit Tracker does not create, edit or delete events in your Google Calendar.</P>
        <P>Once authorised, Habit Tracker may periodically synchronise your selected Calendar information in the background, and you may also trigger a synchronisation manually at any time.</P>
        <P>To maintain the connection without requiring you to sign in repeatedly, Habit Tracker may securely retain the OAuth access and refresh credentials issued by Google for as long as the integration remains authorised.</P>
        <P>Google Calendar data obtained through this integration is used only to provide Habit Tracker functionality. It is not sold, it is not used for advertising, and it is not used to build advertising profiles. Where service providers process information on our behalf to operate or support Xenwealth Habit Tracker, such processing is subject to the disclosures and safeguards described in this Privacy Policy, including section 5 above.</P>
        <P>You may disconnect the Google Calendar integration from within Habit Tracker at any time. You may also revoke Habit Tracker's access directly from your Google Account permissions.</P>
        <P last>Google Calendar information is retained in line with section 7 above — only for as long as reasonably necessary to provide the relevant functionality, satisfy legitimate operational requirements, or meet legal obligations. See also our <a href="/terms" style={{ color: "#b9860a", fontWeight: 600 }}>Terms of Service</a> for the terms governing use of Habit Tracker.</P>
        <Callout style={{ marginTop: "20px", marginBottom: 0 }}>Xenwealth Habit Tracker's use and transfer to any other app of information received from Google APIs will adhere to the <a href="https://developers.google.com/terms/api-services-user-data-policy" style={{ color: "#b9860a", fontWeight: 600 }} target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.</Callout>
      </Section>

      <Section id="p13" heading="13. Contact us">
        <P last>Questions about this policy can be sent to the details below.</P>
      </Section>
    </LegalLayout>
  );
}
